import React from 'react';
import { FieldLLMConfig } from '../../types/schema';
import { Tooltip } from '../ui/Tooltip';

const FIELD_TOOLTIPS: Record<string, string> = {
  description: `Le plus important - définit le sens du champ.

Utilisé dans:
• Le JSON Schema (champ "description")
• Les instructions Markdown envoyées au LLM

Exemple: "Prix de vente total de la transaction"`,

  format: `Indique comment formater la valeur de sortie.

Génère: "- Format: {valeur}"

Exemples:
• "nombre entier sans espaces"
• "YYYY-MM-DD"
• "chiffres seulement"`,

  search_terms: `Aide le LLM à localiser l'information dans le document.

Génère: "- Chercher: "term1", "term2""

Améliore le rappel en fournissant des synonymes et variantes.

Exemples: "prix", "montant", "considération"`,

  examples_valid: `Exemples concrets de bonnes réponses (few-shot learning).

Génère: "- Correct: "ex1", "ex2""

Le LLM apprend le format attendu par l'exemple.`,

  examples_invalid: `Exemples d'erreurs communes à éviter.

Génère: "- Incorrect: "bad1", "bad2""

Aide le LLM à éviter les pièges courants.`,

  allowed_values: `Contraint les valeurs possibles (enum).

Utilisé dans:
• Le JSON Schema (champ "enum")
• Les instructions: "- Valeurs permises: "val1", "val2""

Améliore la précision en forçant des valeurs valides.`,

  notes: `Avertissements et cas particuliers.

Génère: "- {notes}"

Pour les règles spéciales, exceptions, et edge cases.`
};

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip content={text} maxWidth="max-w-2xl" className="text-blue-600 ml-1" ariaLabel="Aide">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  </Tooltip>
);

interface Props {
  config: FieldLLMConfig;
  onChange: (config: FieldLLMConfig) => void;
}

const LLMInstructionsEditor: React.FC<Props> = ({ config, onChange }) => {
  const update = (key: keyof FieldLLMConfig, value: any) => {
    onChange({ ...config, [key]: value });
  };

  const updateArray = (key: 'search_terms' | 'examples_valid' | 'examples_invalid' | 'allowed_values', value: string) => {
    const items = value.split(',').map(s => s.trim()).filter(Boolean);
    onChange({ ...config, [key]: items.length > 0 ? items : [] });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description (pour le LLM)
          <InfoTooltip text={FIELD_TOOLTIPS.description} />
        </label>
        <textarea
          value={config.description}
          onChange={(e) => update('description', e.target.value)}
          className="w-full border rounded px-3 py-2 h-20"
          placeholder="Description du champ pour guider l'extraction"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Format attendu
          <InfoTooltip text={FIELD_TOOLTIPS.format} />
        </label>
        <input
          type="text"
          value={config.format || ''}
          onChange={(e) => update('format', e.target.value)}
          className="w-full border rounded px-3 py-2"
          placeholder="Ex: chiffres seulement, sans espaces"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Termes de recherche (séparés par virgule)
          <InfoTooltip text={FIELD_TOOLTIPS.search_terms} />
        </label>
        <input
          type="text"
          value={(config.search_terms || []).join(', ')}
          onChange={(e) => updateArray('search_terms', e.target.value)}
          className="w-full border rounded px-3 py-2"
          placeholder="Acte numéro, No d'acte, Numéro d'inscription"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Exemples valides (virgule)
            <InfoTooltip text={FIELD_TOOLTIPS.examples_valid} />
          </label>
          <input
            type="text"
            value={(config.examples_valid || []).join(', ')}
            onChange={(e) => updateArray('examples_valid', e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="29189684, 12345678"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Exemples invalides (virgule)
            <InfoTooltip text={FIELD_TOOLTIPS.examples_invalid} />
          </label>
          <input
            type="text"
            value={(config.examples_invalid || []).join(', ')}
            onChange={(e) => updateArray('examples_invalid', e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="29 189 684, Acte #123"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valeurs permises (enum, séparées par virgule)
          <InfoTooltip text={FIELD_TOOLTIPS.allowed_values} />
        </label>
        <input
          type="text"
          value={(config.allowed_values || []).join(', ')}
          onChange={(e) => updateArray('allowed_values', e.target.value)}
          className="w-full border rounded px-3 py-2"
          placeholder="Garantie légale, Sans garantie, Garantie sur propriété seulement"
        />
        <p className="text-xs text-gray-500 mt-1">Laisser vide si pas d'enum</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes / Warnings
          <InfoTooltip text={FIELD_TOOLTIPS.notes} />
        </label>
        <textarea
          value={config.notes || ''}
          onChange={(e) => update('notes', e.target.value)}
          className="w-full border rounded px-3 py-2 h-16"
          placeholder="NE JAMAIS utiliser Oui/Non"
        />
      </div>
    </div>
  );
};

export default LLMInstructionsEditor;
